import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  FileText,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Menu } from '@/components/menu';
import { Pager } from '@/components/pager';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  approvableDeleteWarning,
  useAllApprovables,
  useDeleteAnyApprovable,
} from '@/lib/approvables';
import { formatDateLong, formatMoney, formatStatus } from '@/lib/format';
import { PageHeader } from '@/components/page-header';
import { PickClientDialog } from '@/components/pick-client-dialog';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import type { ApprovableData } from '@/types/api';

const TYPE_LABEL: Record<string, string> = {
  proposal: 'Proposal',
  hardware_auth: 'Hardware auth',
};

const TYPE_ICON: Record<string, ComponentType<LucideProps>> = {
  proposal: FileText,
  hardware_auth: ShieldCheck,
};

const TYPE_BUCKET: Record<string, string> = {
  proposal: 'bg-violet-500/12 text-violet-700 dark:text-violet-300',
  hardware_auth: 'bg-amber-500/12 text-amber-700 dark:text-amber-300',
};

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  if (status === 'approved' || status === 'paid') return 'success';
  if (status === 'deferred') return 'warning';
  if (status === 'declined' || status === 'expired') return 'danger';
  if (status === 'sent') return 'info';
  return 'neutral';
}

export function ApprovablesIndex() {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pickOpen, setPickOpen] = useState(false);
  const { data: paginated, isLoading } = useAllApprovables({
    q: q || undefined,
    type: type || undefined,
    status: status || undefined,
    page,
  });
  const items = paginated?.data;
  const meta = paginated?.meta;
  const del = useDeleteAnyApprovable();
  const confirm = useConfirm();
  const toast = useToast();

  /**
   * Deletable at any status, signed and paid included, for records that
   * shouldn't exist at all rather than ones that merely fell through.
   */
  async function deleteApprovable(approvable: ApprovableData) {
    const ok = await confirm({
      title: `Delete ${approvable.title}?`,
      body: approvableDeleteWarning(approvable),
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await del.mutateAsync({ clientId: approvable.client_id, approvableId: approvable.id });
      toast.success(`${approvable.title} deleted`);
    } catch (err) {
      toast.error('Something went wrong', err instanceof Error ? err.message : undefined);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="Approvables"
        subtitle="Every proposal and hardware authorization across all clients."
        action={
          <Button onClick={() => setPickOpen(true)}>
            <Plus className="size-4" /> New approvable
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by title or recipient…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </Select>
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="max-w-[160px]"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="deferred">Deferred</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="declined">Declined</option>
          <option value="expired">Expired</option>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Nothing here yet"
          description="Pick a client to create the first proposal."
          action={
            <Button onClick={() => setPickOpen(true)}>
              <Plus className="size-4" /> New approvable
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {items.map((a) => {
              const Icon = TYPE_ICON[a.type] ?? FileText;
              const bucket = TYPE_BUCKET[a.type] ?? 'bg-muted text-muted-foreground';
              return (
                <DataListRow
                  key={a.id}
                  onClick={() => a.public_url && window.open(a.public_url, '_blank')}
                  interactive={!!a.public_url}
                >
                  <div className={`flex size-8 shrink-0 items-center justify-center rounded-md ${bucket}`}>
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate font-medium text-foreground">{a.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {TYPE_LABEL[a.type] ?? a.type}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <Link
                        to={`/clients/${a.client_id}`}
                        className="hover:text-foreground hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {a.client_name ?? `Client #${a.client_id}`}
                      </Link>
                      {a.recipient_email ? (
                        <>
                          <span aria-hidden>·</span>
                          <span className="truncate">to {a.recipient_email}</span>
                        </>
                      ) : null}
                      {a.sent_at ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>sent {formatDateLong(a.sent_at)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="hidden text-right md:block">
                    <div className="text-sm font-medium tabular-nums text-foreground">
                      {a.total_cents != null ? formatMoney(a.total_cents, a.currency) : '—'}
                    </div>
                  </div>
                  <StatusPill tone={statusTone(a.status)}>{formatStatus(a.status)}</StatusPill>
                  {a.public_url ? (
                    <a
                      href={a.public_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Open ${a.title} in a new tab`}
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100"
                    >
                      <ExternalLink className="size-4" />
                    </a>
                  ) : null}
                  <div onClick={(e) => e.stopPropagation()}>
                    <Menu
                      triggerLabel={`Actions for ${a.title}`}
                      items={[
                        {
                          key: 'delete',
                          label: 'Delete',
                          icon: Trash2,
                          danger: true,
                          onSelect: () => void deleteApprovable(a),
                        },
                      ]}
                    />
                  </div>
                </DataListRow>
              );
            })}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
      <PickClientDialog
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        title="New approvable"
        tab="approvables"
      />
    </div>
  );
}
